/**
 * Argus — LiveAudioPlayer: MSE wrapper pentru audio live (AAC-LC).
 *
 * Codec: AAC-LC `audio/mp4; codecs="mp4a.40.2"`. Aliniat cu LiveScreenPlayer dar `<audio>` element.
 */

import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react';
import type { LivePlayerHandle } from './LiveScreenPlayer';
import { getOperatorWsUrl } from '../lib/streams';

interface Props {
  streamId: string;
  onTerminated: (reason: string) => void;
  muted?: boolean;
  volume?: number; // 0..1 — bloc webrtc-audio-support 2026-05-11
}

const HEADER_SIZE = 24;
const CODEC = 'audio/mp4; codecs="mp4a.40.2"';
const MAX_QUEUE = 50;

function LiveAudioPlayerImpl({ streamId, onTerminated, muted = false, volume = 1 }: Props, ref: React.ForwardedRef<LivePlayerHandle>): JSX.Element {
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const [lag, setLag] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useImperativeHandle(ref, () => ({
    getMediaElement: () => audioRef.current,
    captureStream: () => {
      const a = audioRef.current as (HTMLAudioElement & { captureStream?: () => MediaStream }) | null;
      if (!a) return null;
      try { return a.captureStream?.() ?? null; }
      catch (err) { console.warn('captureStream() failed (audio)', err); return null; }
    },
  }), []);

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    const ms = new MediaSource();
    audio.src = URL.createObjectURL(ms);
    let sb: SourceBuffer | null = null;
    const queue: ArrayBuffer[] = [];
    let droppedCount = 0;
    let ws: WebSocket | null = null;
    let cancelled = false;

    const flushQueue = () => {
      if (sb && !sb.updating && queue.length > 0) {
        const chunk = queue.shift();
        if (chunk) {
          try { sb.appendBuffer(chunk); }
          catch (err) { console.warn('SourceBuffer.appendBuffer failed', err); }
        }
      }
    };

    ms.addEventListener('sourceopen', () => {
      try {
        sb = ms.addSourceBuffer(CODEC);
        sb.mode = 'segments';
        sb.addEventListener('updateend', flushQueue);
      } catch {
        setError('AAC-LC codec not supported in this browser.');
        return;
      }

      ws = new WebSocket(getOperatorWsUrl());
      ws.binaryType = 'arraybuffer';
      ws.addEventListener('open', () => {
        ws?.send(JSON.stringify({ type: 'subscribe_stream', streamId }));
      });
      ws.addEventListener('message', (e) => {
        if (typeof e.data === 'string') {
          try {
            const json = JSON.parse(e.data);
            if (json.type === 'stream_terminated') { onTerminated(json.reason ?? 'unknown'); return; }
            if (json.type === 'stream_lag') setLag(true);
          } catch {}
          return;
        }
        if (cancelled) return;
        const ab = e.data as ArrayBuffer;
        if (ab.byteLength < HEADER_SIZE) return;
        const payload = ab.slice(HEADER_SIZE);
        queue.push(payload);
        if (queue.length > MAX_QUEUE) {
          queue.splice(0, 25);
          droppedCount += 25;
          setLag(true);
          console.warn(`Audio stream lag — dropped 25 chunks (total ${droppedCount})`);
        }
        flushQueue();
      });
      ws.addEventListener('close', () => {
        if (!cancelled) onTerminated('ws_closed');
      });
      ws.addEventListener('error', () => {
        if (!cancelled) setError('Stream connection error.');
      });
    });

    audio.play().catch(() => {});
    audio.muted = muted;
    audio.volume = Math.max(0, Math.min(1, volume));

    return () => {
      cancelled = true;
      try { ws?.send(JSON.stringify({ type: 'unsubscribe_stream', streamId })); ws?.close(); } catch {}
      try { if (ms.readyState === 'open') ms.endOfStream(); } catch {}
      try { URL.revokeObjectURL(audio.src); } catch {}
    };
  }, [streamId, onTerminated]);

  // Bloc webrtc-audio-support 2026-05-11: propagă muted + volume dinamic la `<audio>` element.
  useEffect(() => {
    const a = audioRef.current;
    if (a) {
      a.muted = muted;
      a.volume = Math.max(0, Math.min(1, volume));
    }
  }, [muted, volume]);

  return (
    <div className="live-audio-player">
      {error !== null && <div className="stream-error">{error}</div>}
      {lag && <div className="stream-lag-badge">Lag detected</div>}
      <div className="audio-meter">🔊 Live audio</div>
      <audio ref={audioRef} autoPlay controls />
    </div>
  );
}

const LiveAudioPlayer = forwardRef<LivePlayerHandle, Props>(LiveAudioPlayerImpl);
export default LiveAudioPlayer;
